import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardTitle, Col, Container, Row } from 'reactstrap';
import { useFormik } from 'formik';
import { useLocation, Link } from 'react-router-dom';

import AppBreadcrumb from 'components/App/Breadcrumb';
import AppButton from 'components/App/Button';
import FormField from 'components/AddLocation/FormField';
import Loading from 'components/App/Loading';
import AppMeta from 'components/App/Meta';
import AppInputNumberValidation from 'components/App/InputNumberValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import ModalEditFacilityService from 'components/Modals/ModalEditFacilityService';
import ModalEditParking from 'components/Modals/ModalEditParking';

import { LOCATIONS, themeColor } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { LANGUAGE_LABEL, PARAM_SETTING } from 'constants/setting';

import { useBoolean } from 'usehooks-ts';
import { Search, Info, X } from 'lucide-react';
import { parseCurrency, unparseCurrency } from 'utils';
import validationConvenienceForm from './form';

import styles from './ConveniencesServices.module.scss';

function ConveniencesServices(props) {
  const {
    isLoadingGetSetting,
    isLoadingGetPlaceDetail,
    isLoadingEditPlaceService,
    isLoadingEditPlaceParkingService,

    setting,
    placeDetail,

    actionGetPlaceDetail,
    actionPutPlaceServices,
    actionPutPlaceParkingService,
    actionResetPlaceDetail,
  } = props;

  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');

  const breadcrumbList = [
    {
      name: 'Chỗ nghỉ',
      isActive: false,
      link: `${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`,
    },
    {
      name: 'Tiện ích và dịch vụ',
      isActive: true,
      link: '',
    },
  ];

  const {
    value: isOpenModalService,
    setTrue: setOpenModalService,
    setFalse: setCloseModalService,
  } = useBoolean(false);

  const {
    value: isOpenParkingModal,
    setTrue: setOpenParkingModal,
    setFalse: setCloseParkingModal,
  } = useBoolean(false);

  const [searchTerm, setSearchTerm] = React.useState('');

  const languageOptions = React.useMemo(() => {
    if (setting[PARAM_SETTING.LANGUAGES]) {
      const newLanguages = setting[PARAM_SETTING.LANGUAGES].map((item) => ({
        label: LANGUAGE_LABEL[item.name],
        value: item.name,
      }));

      return newLanguages;
    }

    return [];
  }, [setting]);

  const yearOptions = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = currentYear; y >= 1900; y -= 1) {
      years.push({ label: `${y}`, value: y });
    }
    return years;
  }, []);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      totalFloors: placeDetail.totalFloors,
      totalRooms: placeDetail.totalRooms,
      openedYear: (placeDetail.openedYear && +placeDetail.openedYear)
        || (placeDetail.yearOpened && +placeDetail.yearOpened)
        || '',
      renovatedYear: (placeDetail.renovatedYear && +placeDetail.renovatedYear)
        || (placeDetail.yearRenovated && +placeDetail.yearRenovated)
        || '',
      mainLanguages: placeDetail.mainLanguages?.map((item) => ({
        label: LANGUAGE_LABEL[item],
        value: item,
      })),
      serviceList: setting[PARAM_SETTING.SERVICES]?.filter(
        (item) => item.name !== 'LANGUAGE',
      ),
      serviceOptionEdit: [],
      parking: {
        hasParking: false,
        parkingTypes: [],
        parkingLocations: [],
        parkingAccessTypes: '',
        parkingReservation: '',
        isPaid: false,
        price: 0,
        accessible: false,
        valet: false,
      },
    },
    validationSchema: validationConvenienceForm,

    onSubmit: (values) => {
      const listActivedServices = values.serviceList.map((item) => {
        const serviceChildrenActive = [];

        item.children.forEach((itemChildren) => {
          if (itemChildren.isActive) {
            serviceChildrenActive.push(itemChildren.name);
          }
        });

        if (serviceChildrenActive) {
          return {
            group: item.name,
            children: serviceChildrenActive,
          };
        }

        return {};
      });

      const payload = {
        totalRooms: parseCurrency(values.totalRooms),
        totalFloors: parseCurrency(values.totalFloors),
        mainLanguages: values.mainLanguages.map((item) => item.value),
        services: listActivedServices,
      };

      const openedYearVal = values.openedYear?.value ?? values.openedYear;
      if (openedYearVal) {
        payload.openedYear = openedYearVal;
      }

      const renovatedYearVal = values.renovatedYear?.value ?? values.renovatedYear;
      if (renovatedYearVal) {
        payload.renovatedYear = renovatedYearVal;
      }

      actionPutPlaceServices({
        placeId,
        values: payload,
      });
    },
  });

  // MODAL SERVICE OPTIONS
  const onOpenServiceOptions = React.useCallback((service) => {
    setOpenModalService();
    validation.setFieldValue('serviceOptionEdit', service, false);
  }, []);

  const onToggleCheckedService = React.useCallback(
    (e) => {
      const { value } = e.target;

      validation.setFieldValue(
        'serviceOptionEdit',
        {
          ...validation.values.serviceOptionEdit,
          children: validation.values.serviceOptionEdit.children.map((item) => {
            if (item.name === value) {
              return {
                ...item,
                isActive: !item.isActive,
              };
            }

            return {
              ...item,
            };
          }),
        },
        false,
      );
    },
    [validation.values],
  );

  const onToggleAllService = React.useCallback(
    (e) => {
      const { value } = e.target;

      validation.setFieldValue(
        'serviceOptionEdit',
        {
          ...validation.values.serviceOptionEdit,
          children: validation.values.serviceOptionEdit.children.map(
            (item) => ({
              ...item,
              isActive: +value === 1,
            }),
          ),
        },
        false,
      );
    },
    [validation.values],
  );

  const onConfirmModal = React.useCallback(() => {
    setCloseModalService();
    validation.setFieldValue(
      'serviceList',
      validation.values.serviceList.map((service) => {
        if (service.name === validation.values.serviceOptionEdit.name) {
          return validation.values.serviceOptionEdit;
        }
        return service;
      }),
      false,
    );

    validation.setFieldValue('serviceOptionEdit', {}, false);
  }, [validation.values]);

  const onCancelModal = React.useCallback(() => {
    setCloseModalService();
    validation.setFieldValue('serviceOptionEdit', {}, false);
  }, [validation.values]);

  const onChangeLanguage = React.useCallback(
    (selected) => {
      validation.setFieldValue('mainLanguages', selected, true);
    },
    [validation],
  );

  const onChangeNumber = React.useCallback((e) => {
    const { name, value } = e.target;

    validation.setFieldValue(
      name,
      value.length > 0 ? parseCurrency(value) : 0,
      false,
    );
  }, []);

  const onChangeSelect = React.useCallback(
    (e) => {
      validation.setFieldValue(e.name, e?.value ?? '', false);
    },
    [validation],
  );

  const RenderActiveServiceOptions = React.useCallback(
    (services) => {
      const activeList = services?.filter((item) => item.isActive) || [];

      return (
        <div className={styles.CardBody}>
          {activeList.map((item) => (
            <span
              key={item.name || item.vi}
              className={styles.TagPill}
            >
              {item.vi}
              {item.hasDetail && (
                <span className={styles.TagInfoIcon}>
                  <Info size={13} />
                </span>
              )}
            </span>
          ))}
        </div>
      );
    },
    [],
  );

  const fallbackParkingServiceSetting = React.useMemo(() => [
    {
      name: 'PARKING_TYPES',
      vi: 'Loại bãi đậu xe',
      en: 'Parking type',
      children: [
        { name: 'INDOOR', vi: 'Trong nhà', en: 'Indoor' },
        { name: 'OUTDOOR', vi: 'Ngoài trời', en: 'Outdoor' },
        { name: 'STREET', vi: 'Ngoài đường', en: 'On-street' },
      ],
    },
    {
      name: 'PARKING_LOCATIONS',
      vi: 'Bãi đậu xe này nằm ở đâu?',
      en: 'Where is this parking located?',
      children: [
        { name: 'ON_SITE', vi: 'Trong khuôn viên', en: 'On-site' },
        { name: 'OFF_SITE', vi: 'Ngoài khuôn viên', en: 'Off-site' },
      ],
    },
    {
      name: 'PARKING_ACCESS_TYPES',
      vi: 'Ai có thể đậu xe ở đây?',
      en: 'Who can park here?',
      children: [
        { name: 'GUESTS_ONLY', vi: 'Chỉ dành cho khách', en: 'Guests only' },
        { name: 'EVERYONE', vi: 'Dành cho tất cả mọi người', en: 'For everyone' },
      ],
    },
    {
      name: 'PARKING_RESERVATION',
      vi: 'Khách đặt chỗ trước',
      en: 'Guest reservation',
      children: [
        { name: 'REQUIRED', vi: 'Bắt buộc', en: 'Required' },
        { name: 'OPTIONAL', vi: 'Không bắt buộc', en: 'Optional' },
      ],
    },
  ], []);

  const RenderParkingDescription = React.useCallback(() => {
    const { parking } = validation.values;
    if (!parking || !parking.hasParking) {
      return (
        <div className="Description__Content">
          Không có chỗ đậu xe
        </div>
      );
    }

    const activeList = [];

    const getSettingByName = (name) => {
      const found = (setting.parkingService || []).find((item) => item.name === name);
      if (found && found.children && found.children.length > 0) {
        return found.children;
      }
      return (
        fallbackParkingServiceSetting.find((item) => item.name === name)?.children || []
      );
    };

    // Types
    const typesSetting = getSettingByName('PARKING_TYPES');
    parking.parkingTypes?.forEach((name) => {
      const match = typesSetting.find((item) => item.name === name);
      if (match) activeList.push(match.vi);
    });

    // Locations
    const locationsSetting = getSettingByName('PARKING_LOCATIONS');
    parking.parkingLocations?.forEach((name) => {
      const match = locationsSetting.find((item) => item.name === name);
      if (match) activeList.push(match.vi);
    });

    // Access
    const accessSetting = getSettingByName('PARKING_ACCESS_TYPES');
    const accessMatch = accessSetting.find((item) => item.name === parking.parkingAccessTypes);
    if (accessMatch) activeList.push(accessMatch.vi);

    // Reservation
    const reservationSetting = getSettingByName('PARKING_RESERVATION');
    const reservationMatch = reservationSetting.find(
      (item) => item.name === parking.parkingReservation,
    );
    if (reservationMatch) activeList.push(reservationMatch.vi);

    // Paid
    if (parking.isPaid) {
      activeList.push(`Có tính phí (${unparseCurrency(parking.price)} VNĐ)`);
    } else {
      activeList.push('Miễn phí');
    }

    // Accessible
    if (parking.accessible) {
      activeList.push('Hỗ trợ người khuyết tật');
    }

    // Valet
    if (parking.valet) {
      activeList.push('Nhân viên đậu xe cho khách');
    }

    return (
      <div className="Description__Content">
        {activeList.length > 0 ? activeList.join(', ') : '-'}
      </div>
    );
  }, [validation.values.parking, setting.parkingService, fallbackParkingServiceSetting]);

  const onOpenParkingOptions = React.useCallback(() => {
    setOpenParkingModal();
  }, []);

  const onConfirmParkingModal = React.useCallback(
    (updatedParking) => {
      setCloseParkingModal();
      actionPutPlaceParkingService({
        placeId,
        values: {
          parkingService: {
            hasParking: updatedParking.hasParking,
            parkingTypes: updatedParking.parkingTypes,
            locations: updatedParking.parkingLocations,
            accessType: updatedParking.parkingAccessTypes,
            reservation: updatedParking.parkingReservation,
            isPaid: updatedParking.isPaid,
            price: updatedParking.price,
            isAccessible: updatedParking.accessible,
            hasValet: updatedParking.valet,
          },
        },
        callback: () => {
          actionGetPlaceDetail({ placeId });
        },
      });
    },
    [placeId, actionPutPlaceParkingService, actionGetPlaceDetail],
  );

  React.useEffect(() => {
    actionGetPlaceDetail({
      placeId,
    });

    return () => {
      actionResetPlaceDetail();
    };
  }, [placeId, setting]);

  React.useEffect(() => {
    if (placeDetail._id) {
      validation.setFieldValue(
        'serviceList',
        validation.values.serviceList?.map((service) => {
          const newServiceMatch = placeDetail.services?.find(
            (item) => item.group === service.name,
          );

          if (newServiceMatch?.group === service.name) {
            return {
              ...service,
              children: service.children.map((item) => ({
                ...item,
                isActive: newServiceMatch.children.includes(item.name),
              })),
            };
          }

          return service;
        }),
        true,
      );

      const parkingData = placeDetail.parkingService || placeDetail.parking || {};
      validation.setFieldValue(
        'parking',
        {
          hasParking: parkingData.hasParking ?? false,
          parkingTypes: parkingData.parkingTypes ?? [],
          parkingLocations:
            parkingData.locations || parkingData.parkingLocations || [],
          parkingAccessTypes:
            parkingData.accessType || parkingData.parkingAccessTypes || '',
          parkingReservation:
            parkingData.reservation || parkingData.parkingReservation || '',
          isPaid: parkingData.isPaid ?? false,
          price: parkingData.price ?? 0,
          accessible:
            parkingData.isAccessible ?? parkingData.accessible ?? false,
          valet: parkingData.hasValet ?? parkingData.valet ?? false,
        },
        true,
      );
    }
  }, [placeDetail]);

  return (
    <div className={`page-content ${styles.Page}`}>
      <Loading
        loaderOnPage
        loading={
          isLoadingGetSetting ||
          isLoadingEditPlaceService ||
          isLoadingEditPlaceParkingService ||
          isLoadingGetPlaceDetail
        }
      />

      <AppMeta name="Tiện ích và dịch vụ" />

      <form onSubmit={validation.handleSubmit}>
        <Container fluid>
          <AppBreadcrumb
            pageTitle="Tiện ích và dịch vụ"
            breadcrumbList={breadcrumbList}
          />

          <Card className="Section">
            <CardTitle className="Section__Title">Thông tin chỗ nghỉ</CardTitle>

            <Row className="g-3 mb-3">
              <Col md="3">
                <FormField
                  label="Tổng số tầng chỗ nghỉ có (trừ tầng hầm)"
                  isRequired
                  className="Section__FormField"
                >
                  <AppInputNumberValidation
                    isCustomOnChange
                    thousandSeparator="."
                    decimalSeparator=","
                    name="totalFloors"
                    placeholder="Nhập số tầng"
                    validation={validation}
                    onChange={onChangeNumber}
                  />
                </FormField>
              </Col>

              <Col md="3">
                <FormField
                  label="Tổng số phòng tại chỗ nghỉ"
                  isRequired
                  className="Section__FormField"
                >
                  <AppInputNumberValidation
                    isCustomOnChange
                    name="totalRooms"
                    placeholder="Nhập số phòng"
                    validation={validation}
                    onChange={onChangeNumber}
                  />
                </FormField>
              </Col>

              <Col md="3">
                <FormField
                  label="Năm khai trương"
                  className="Section__FormField"
                >
                  <AppSelectValidation
                    placeholder="Chọn năm"
                    name="openedYear"
                    key="openedYear"
                    options={yearOptions}
                    value={validation.values.openedYear}
                    validation={validation}
                    onChange={onChangeSelect}
                    selectedClassName={styles.YearSelect}
                  />
                </FormField>
              </Col>

              <Col md="3">
                <FormField
                  label="Năm nâng cấp chỗ nghỉ gần nhất"
                  className="Section__FormField"
                >
                  <AppSelectValidation
                    placeholder="Chọn năm"
                    name="renovatedYear"
                    key="renovatedYear"
                    options={yearOptions}
                    value={validation.values.renovatedYear}
                    validation={validation}
                    onChange={onChangeSelect}
                    selectedClassName={styles.YearSelect}
                  />
                </FormField>
              </Col>
            </Row>

            <Row>
              <Col xs="12">
                <FormField
                  label="Ngôn ngữ được sử dụng"
                  className="Section__FormField"
                >
                  <AppSelectValidation
                    isMulti
                    isHiddenClearable
                    placeholder="Chọn ngôn ngữ"
                    name="mainLanguages"
                    key="mainLanguages"
                    options={languageOptions}
                    value={validation.values.mainLanguages}
                    validation={validation}
                    onChangeMulti={onChangeLanguage}
                  />
                </FormField>
              </Col>
            </Row>
          </Card>

          <Card className="Section">
            <div>
              <h4 className={styles.AmenitiesTitle}>
                Danh mục Tiện ích & Dịch vụ
              </h4>
              <p className={styles.AmenitiesSubtitle}>
                <span>Những tiện ích </span>
                <span className={styles.InfoIconInline}>
                  <Info size={13} />
                </span>
                <span> có thể thêm những thông tin chi tiết khi bấm vào.</span>
              </p>

              <div className={styles.SearchContainer}>
                <Search className={styles.SearchIcon} size={15} />
                <input
                  type="text"
                  className={styles.SearchInput}
                  placeholder="Tìm kiếm tiện ích. VD: hồ bơi,..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                <button
                  type="button"
                  className={styles.SearchClearBtn}
                  onClick={() => setSearchTerm('')}
                >
                  <X size={15} />
                </button>
                )}
              </div>
            </div>

            {(() => {
              const half = Math.round(
                (validation.values.serviceList?.length || 0) / 2,
              );
              const col1 = validation.values.serviceList?.slice(0, half) || [];
              const col2 = validation.values.serviceList?.slice(half) || [];
              const maxLen = Math.max(col1.length, col2.length);

              const rows = [];
              for (let i = 0; i < maxLen; i += 1) {
                rows.push({
                  itemLeft: col1[i],
                  itemRight: col2[i],
                  index: i,
                });
              }

              const term = searchTerm.toLowerCase().trim();
              const filterMatch = (service) => {
                if (!service) return false;
                if (!term) return true;
                return (
                  service.vi?.toLowerCase().includes(term) ||
                service.children?.some((c) =>
                  c.vi?.toLowerCase().includes(term),
                )
                );
              };

              const filteredRows = rows.filter(
                (r) => filterMatch(r.itemLeft) || filterMatch(r.itemRight),
              );

              if (filteredRows.length === 0) {
                return (
                  <div className={styles.EmptySearchResult}>
                    <span>Không tìm thấy tiện ích nào phù hợp</span>
                    {searchTerm ? <span>{` với "${searchTerm}"`}</span> : null}
                  </div>
                );
              }

              return filteredRows.map((r) => (
                <Row className="g-3 mb-3" key={r.itemLeft?.name || r.index}>
                  <Col md="6" className="d-flex">
                    {r.itemLeft ? (
                      <div className={styles.ServiceCard}>
                        <div className={styles.CardHeader}>
                          <h6 className={styles.CategoryTitle}>
                            {r.itemLeft.vi}
                          </h6>
                          <button
                            type="button"
                            className={styles.EditButton}
                            value={r.itemLeft}
                            onClick={() => onOpenServiceOptions(r.itemLeft)}
                          >
                            Chỉnh sửa
                          </button>
                        </div>

                        {RenderActiveServiceOptions(r.itemLeft.children)}
                      </div>
                    ) : (
                      <div className="w-100" />
                    )}
                  </Col>

                  <Col md="6" className="d-flex">
                    {r.itemRight ? (
                      <div className={styles.ServiceCard}>
                        <div className={styles.CardHeader}>
                          <h6 className={styles.CategoryTitle}>
                            {r.itemRight.vi}
                          </h6>
                          <button
                            type="button"
                            className={styles.EditButton}
                            value={r.itemRight}
                            onClick={() => onOpenServiceOptions(r.itemRight)}
                          >
                            Chỉnh sửa
                          </button>
                        </div>

                        {RenderActiveServiceOptions(r.itemRight.children)}
                      </div>
                    ) : (
                      <div className="w-100" />
                    )}
                  </Col>
                </Row>
              ));
            })()}

            {(!validation.values.serviceList?.some(
              (s) =>
                s.name === 'PARKING' || s.vi?.toLowerCase().includes('đỗ xe'),
            ) ||
            validation.values.parking?.hasParking) && (
            <Row className="g-3 mb-3" key="parking_service_row">
              <Col md="6" className="d-flex">
                <div className={styles.ServiceCard}>
                  <div className={styles.CardHeader}>
                    <h6 className={styles.CategoryTitle}>Đậu xe</h6>
                    <button
                      type="button"
                      className={styles.EditButton}
                      onClick={onOpenParkingOptions}
                    >
                      Chỉnh sửa
                    </button>
                  </div>

                  <div className={styles.CardBody}>
                    <span className="Description">
                      {RenderParkingDescription()}
                    </span>
                  </div>
                </div>
              </Col>
              <Col md="6" className="d-flex" />
            </Row>
            )}
          </Card>

          <Card className="Section SectionFooter">
            <div className="d-flex justify-content-end">
              <Link
                to={`${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${placeId}`}
              >
                <AppButton
                  color={themeColor.SECONDARY}
                  classes="ButtonAction mr-24"
                >
                  Hủy
                </AppButton>
              </Link>

              <AppButton type="submit" classes="ButtonAction">
                Lưu
              </AppButton>
            </div>
          </Card>
        </Container>
      </form>

      <ModalEditFacilityService
        isOpen={isOpenModalService}
        service={validation.values.serviceOptionEdit}
        onCancelModal={onCancelModal}
        onConfirmModal={onConfirmModal}
        onToggleCheckedService={onToggleCheckedService}
        onToggleAllService={onToggleAllService}
      />

      <ModalEditParking
        isOpen={isOpenParkingModal}
        parkingSetting={setting.parkingService}
        parking={validation.values.parking}
        onCancelModal={setCloseParkingModal}
        onConfirmModal={onConfirmParkingModal}
      />
    </div>
  );
}

ConveniencesServices.propTypes = {
  isLoadingGetSetting: PropTypes.bool,
  isLoadingGetPlaceDetail: PropTypes.bool,
  isLoadingEditPlaceService: PropTypes.bool,
  isLoadingEditPlaceParkingService: PropTypes.bool,

  setting: PropTypes.instanceOf(Object),
  placeDetail: PropTypes.instanceOf(Object),

  actionGetPlaceDetail: PropTypes.func,
  actionPutPlaceServices: PropTypes.func,
  actionPutPlaceParkingService: PropTypes.func,
  actionResetPlaceDetail: PropTypes.func,
};

ConveniencesServices.defaultProps = {
  isLoadingGetSetting: false,
  isLoadingGetPlaceDetail: false,
  isLoadingEditPlaceService: false,
  isLoadingEditPlaceParkingService: false,

  setting: INITIAL_VALUE.OBJECT,
  placeDetail: INITIAL_VALUE.OBJECT,

  actionGetPlaceDetail: INITIAL_VALUE.FUNCTION,
  actionPutPlaceServices: INITIAL_VALUE.FUNCTION,
  actionPutPlaceParkingService: INITIAL_VALUE.FUNCTION,
  actionResetPlaceDetail: INITIAL_VALUE.FUNCTION,
};

export default ConveniencesServices;
